import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Axios from 'axios';
import * as Yup from 'yup';
import { Formik } from 'formik';

import {
  Box,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Chip,
  Autocomplete
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

import { useAuth } from 'src/hooks/useAuth';
import { useConfig } from 'src/hooks/useConfig';
import { axiosHeaders, getLocalisedErrorString, appendContactSupport } from 'src/utils/helpers';
import authService from 'src/utils/authService';
import SubmitBtn from 'src/shared/buttons/SubmitBtn';
import CancelBtn from 'src/shared/buttons/CancelBtn';
import styles from './style';

interface PropTypes {
  open: boolean;
  handleClose: () => void;
  onWorkflowCreated?: () => void;
}

const AddNewWorkflow = (props: PropTypes) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { API, LOGIN_PANEL_URL } = useConfig();

  const { open, handleClose, onWorkflowCreated } = props;

  const [projects, setProjects] = useState<Record<string, any>[]>([]);
  const [users, setUsers] = useState<Record<string, any>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [projectSearchText, setProjectSearchText] = useState('');

  useEffect(() => {
    if (open) {
      fetchProjects();
      fetchUsers();
    }
  }, [open]);

  const fetchProjects = async () => {
    if (!authService.validateToken()) {
      enqueueSnackbar(t('PROCYS_LOGIN_SESSION_EXPIRED'), {
        variant: 'error',
      });
      setTimeout(() => {
        authService.logout(LOGIN_PANEL_URL);
      }, 2000);
      return;
    }

    setIsLoading(true);
    try {
      const response = await Axios.get(
        `${API.workflowsProjects}/min/${encodeURIComponent(user?.companyID || '')}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success && response.data.data) {
        const projectsData = response.data.data
          .filter((item: any) => item.id)
          .map((item: any) => ({
            id: item.id,
            name: item.name,
          }));
        setProjects(projectsData);
      }
    } catch (error: any) {
      enqueueSnackbar(
        appendContactSupport(
          window.config.support_email,
          getLocalisedErrorString(error?.response?.data?.i18n || 'PROJECTS_FETCH_ERROR', t),
          t
        ),
        {
          variant: 'error',
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!authService.validateToken()) {
      return;
    }

    try {
      const response = await Axios.get(
        `${API.getUsersByCompanies}${user?.companyID}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success && response.data.data) {
        const usersData = response.data.data.map((user: any) => ({
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
        }));
        setUsers(usersData);
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
    }
  };

  const createWorkflow = async (values: { name: string; description: string; projectId: string; users: string[] }) => {
    if (!authService.validateToken()) {
      enqueueSnackbar(t('PROCYS_LOGIN_SESSION_EXPIRED'), {
        variant: 'error',
      });
      setTimeout(() => {
        authService.logout(LOGIN_PANEL_URL);
      }, 2000);
      return false;
    }

    try {
      const requestBody = {
        name: values.name,
        description: values.description,
        users: values.users,
      };

      const response = await Axios.post(
        `${API.createProject}/${values.projectId}/workflows`,
        requestBody,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success) {
        enqueueSnackbar(t('WORKFLOW_CREATE_SUCCESS'), {
          variant: 'success',
        });
        if (onWorkflowCreated) {
          onWorkflowCreated();
        }
        return true;
      } else {
        enqueueSnackbar(t('WORKFLOW_CREATE_FAILED'), {
          variant: 'error',
        });
        return false;
      }
    } catch (error: any) {
      console.error('Error creating workflow:', error);
      enqueueSnackbar(
        appendContactSupport(
          window.config.support_email,
          getLocalisedErrorString(error?.response?.data?.i18n || 'WORKFLOW_CREATE_ERROR', t),
          t
        ),
        {
          variant: 'error',
        }
      );
      return false;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={styles.modal}
      onClose={handleClose}
    >
      <Formik
        initialValues={{
          name: '',
          description: '',
          projectId: '',
          users: [] as string[]
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string()
            .min(2, t('DOC_TYPE_NAME_MIN_HELPER_TEXT'))
            .max(64, t('DOC_TYPE_NAME_MAX_HELPER_TEXT'))
            .matches(/^[a-zA-Z0-9][a-zA-Z0-9 _-]*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/, t('WORKFLOW_NAME_INVALID_FORMAT'))
            .required(t('DOC_TYPE_NAME_REQUIRED_HELPER_TEXT')),
          description: Yup.string()
            .min(2, t('WORKFLOWS_DESCRIPTION_MIN_HELPER_TEXT'))
            .max(500, t('DOC_TYPE_DESCRIPTION_MAX_HELPER_TEXT'))
            .required(t('DOC_TYPE_DESCRIPTION_REQUIRED_HELPER_TEXT')),
          projectId: Yup.string()
            .required(t('WORKFLOW_PROJECT_REQUIRED')),
          users: Yup.array()
            .of(Yup.string())
            .min(0, t('WORKFLOW_USERS_REQUIRED'))
        })}
        onSubmit={async (values, {
          resetForm,
          setStatus,
          setSubmitting
        }) => {
          setSubmitting(true);
          const success = await createWorkflow(values);
          if (success) {
            resetForm();
            setStatus({ success: true });
            handleClose();
          }
          setSubmitting(false);
        }}
      >
        {({
          errors,
          isSubmitting,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue
        }) => (
          <form style={{ height: '100%' }}>
            <Box sx={styles.container}>
              <Box sx={styles.modalHeader}>
                <Box sx={styles.titleContainer}>
                  <Typography sx={styles.headerTitle}>
                    {t('WORKFLOWS_CREATE_WORKFLOW')}
                  </Typography>
                  <IconButton sx={styles.closeBtn} onClick={handleClose}>
                    <CloseIcon sx={styles.closeIcon} />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={styles.modalContent}>
                <Box sx={styles.addNewForm}>
                  <Box sx={styles.fieldContainer}>
                    <TextField
                      fullWidth
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name ? String(errors.name) : ''}
                      label={t('WORKFLOW_NAME')}
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      required
                      value={values.name}
                      variant="outlined" sx={{
                        ...styles.textField,
                        '& .MuiInputBase-input': {
                          fontSize: '13px',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={styles.fieldContainer}>
                    <TextField
                      fullWidth
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description ? String(errors.description) : ''}
                      name="description"
                      label={t('WORKFLOW_DESCRIPTION')}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      required
                      value={values.description}
                      variant="outlined"
                      multiline
                      maxRows={10}
                      sx={styles.textFieldMulti}
                    />
                  </Box>

                  <Box sx={styles.fieldContainer}>
                    <Autocomplete
                      id="project-select"
                      options={projects}
                      getOptionLabel={(option) => option.name || ''}
                      value={projects.find(p => p.id === values.projectId) || null}
                      onChange={(_, newValue) => {
                        setFieldValue('projectId', newValue ? newValue.id : '');
                      }}
                      disabled={isLoading}
                      inputValue={projectSearchText}
                      onInputChange={(_, newInputValue) => {
                        setProjectSearchText(newInputValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('WORKFLOW_PROJECT')}
                          error={Boolean(touched.projectId && errors.projectId)}
                          helperText={touched.projectId && errors.projectId ? String(errors.projectId) : ''}
                          InputLabelProps={{
                            style: { fontSize: '13px' }
                          }}
                          required
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} style={{ fontSize: '14px' }}>
                          {option.name}
                        </li>
                      )}
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '14px',
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '11px',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={styles.fieldContainer}>
                    <Autocomplete
                      multiple
                      id="users-autocomplete"
                      options={users.filter(user => !values.users.includes(user.email))}
                      value={users.filter(user => values.users.includes(user.email))}
                      onChange={(event, newValue) => {
                        setFieldValue('users', newValue.map(user => user.email));
                      }}
                      getOptionLabel={(option) => option.email}
                      isOptionEqualToValue={(option, value) => option.email === value.email}
                      renderInput={(params) => {
                        const chipsCount = values.users.length;
                        const chipsPerRow = Math.floor(400 / 200);
                        const rows = Math.ceil(chipsCount / chipsPerRow) || 1;
                        const baseHeight = 40;
                        const chipRowHeight = 40;
                        const dynamicHeight = baseHeight + (rows > 1 ? (rows - 1) * chipRowHeight : 0);

                        return (
                          <TextField
                            {...params}
                            label={t('WORKFLOW_ASSIGN_USERS')}
                            error={Boolean(touched.users && errors.users)}
                            helperText={touched.users && errors.users ? String(errors.users) : ''}
                            sx={{
                              ...styles.autocompleteField,
                              '& .MuiInputBase-root': {
                                minHeight: `${dynamicHeight}px`,
                                height: 'auto',
                                alignItems: 'flex-start',
                                paddingTop: chipsCount > 0 ? '8px' : '16.5px',
                                paddingBottom: '8px',
                                transition: 'min-height 0.2s ease-in-out',
                              },
                              '& .MuiInputLabel-root': {
                                transform: chipsCount > 0
                                  ? 'translate(14px, -9px) scale(0.75)'
                                  : 'translate(14px, 16px) scale(1)',
                                '&.MuiInputLabel-shrink': {
                                  transform: 'translate(14px, -9px) scale(0.75)',
                                }
                              }
                            }}
                            FormHelperTextProps={{
                              sx: { fontSize: '11px' }
                            }}
                          />
                        );
                      }}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => {
                          return (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.email}
                              label={option.email}
                              sx={styles.userChip}
                              deleteIcon={<CloseIcon />}
                            />
                          );
                        })
                      }
                      renderOption={(props, option) => (
                        <li {...props} style={{ fontSize: '14px', padding: '10px 16px' }}>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: '400' }}>
                            {option.email}
                          </Typography>
                        </li>
                      )}
                      ListboxProps={{
                        style: {
                          maxHeight: '200px',
                          fontSize: '13px'
                        }
                      }}
                      clearIcon={null}
                      disableCloseOnSelect
                      sx={{
                        ...styles.autocompleteField,
                        '& .MuiAutocomplete-inputRoot': {
                          flexWrap: 'wrap',
                          paddingRight: '39px !important',
                        },
                        '& .MuiAutocomplete-input': {
                          minWidth: '120px',
                          fontSize: '13px',
                        },
                        '& .MuiAutocomplete-tag': {
                          margin: '2px',
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box sx={styles.modalFooter}>
                <Box display="flex" justifyContent="flex-end" gap="16px">
                  <CancelBtn
                    title={t('DOC_TYPES_TABLE_ADD_NEW_CANCEL')}
                    onClick={handleClose}
                  />
                  <SubmitBtn
                    title={t('WORKFLOWS_CREATE_WORKFLOW')}
                    disabled={isSubmitting ||
                      !values.name ||
                      !values.description ||
                      !values.projectId ||
                      Object.keys(errors).length > 0}
                    onClick={handleSubmit}
                  />
                </Box>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
    </Drawer>
  );
}

export default AddNewWorkflow;
